import { Link } from "react-router-dom";
import { TRASignatureEmptyState } from "../../../../design/visual";
import { ACHIEVEMENT_GROUPS } from "../../../../domain/player/playerAchievementFramework";
import styles from "../../styles/playerEcosystem.module.css";

/**
 * Grouped achievement badges with progress.
 * Share-ready surfaces use `data-share` for future export cards (export not implemented).
 * @param {{
 *   framework: {
 *     byGroup?: Record<string, Array<object>>,
 *     unlockedCount?: number,
 *     totalCount?: number,
 *   } | null,
 *   emptyCopy?: { title: string, body: string, ctaLabel?: string, ctaTo?: string } | null,
 * }} props
 */
export default function AchievementGallery({ framework, emptyCopy }) {
  if (!framework?.totalCount) {
    return (
      <TRASignatureEmptyState
        icon="medal"
        title={emptyCopy?.title || "No achievements yet"}
        description={emptyCopy?.body || "Play matches to unlock career badges."}
        action={
          emptyCopy?.ctaTo ? (
            <Link className={styles.linkAction} to={emptyCopy.ctaTo}>
              {emptyCopy.ctaLabel || "Get started"}
            </Link>
          ) : null
        }
      />
    );
  }

  const groups = ["career", "winning", "activity", "sport"];

  return (
    <div className={styles.achGallery} aria-label="Achievements" data-share="achievements">
      <p className={styles.achProgressSummary}>
        {framework.unlockedCount || 0} / {framework.totalCount} unlocked
      </p>
      <div className={styles.achProgressTrack} aria-hidden>
        <div
          className={styles.achProgressFill}
          style={{
            width: `${Math.min(
              100,
              Math.round(((framework.unlockedCount || 0) / framework.totalCount) * 100)
            )}%`,
          }}
        />
      </div>
      {groups.map((gid) => {
        const items = framework.byGroup?.[gid] || [];
        if (!items.length) return null;
        const label = ACHIEVEMENT_GROUPS[gid]?.label || gid;
        return (
          <section key={gid} className={styles.achGroup} aria-labelledby={`ach-${gid}`}>
            <h3 id={`ach-${gid}`} className={styles.sectionTitle}>
              {label}
            </h3>
            <div className={styles.achGrid}>
              {items.map((a) => {
                const pct =
                  a.target > 0
                    ? Math.min(100, Math.round(((a.progress || 0) / a.target) * 100))
                    : a.unlocked
                      ? 100
                      : 0;
                return (
                  <article
                    key={a.id}
                    className={`${styles.achBadge}${a.unlocked ? ` ${styles.achBadgeOn}` : ""}`}
                    data-share={a.unlocked ? "achievement" : undefined}
                  >
                    <div className={styles.achBadgeTop}>
                      <span className={styles.achIcon} aria-hidden>
                        {a.unlocked ? "★" : "○"}
                      </span>
                      <div className={styles.achBadgeBody}>
                        <p className={styles.achBadgeTitle}>{a.title}</p>
                        <p className={styles.muted}>{a.detail}</p>
                        {a.whenLabel ? (
                          <p className={styles.activityWhen}>{a.whenLabel}</p>
                        ) : null}
                      </div>
                    </div>
                    {!a.unlocked && a.target ? (
                      <div className={styles.achMiniTrack} aria-label={`${pct}%`}>
                        <div className={styles.achMiniFill} style={{ width: `${pct}%` }} />
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
